import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Upload, FileIcon, Trash2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { hasFinancialAccess, isManagement } from "../lib/constants";

export default function FileUpload({ bookingId, divisionId, category = "Evidence", onChange }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const canManage = isManagement(user) || user?.role === "division_manager" || user?.role === "division_admin";

  const load = async () => {
    const params = bookingId ? `?booking_id=${bookingId}` : "";
    const { data } = await api.get(`/files${params}`);
    setFiles(data);
    onChange?.(data);
  };
  useEffect(() => { load(); }, [bookingId]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (bookingId) fd.append("booking_id", bookingId);
      if (divisionId) fd.append("division_id", divisionId);
      fd.append("category", category);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("File terunggah"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus file ini?")) return;
    await api.delete(`/files/${id}`); toast.success("Terhapus"); load();
  };
  const toggleVisibility = async (f) => {
    try {
      await api.put(`/files/${f.id}/visibility`, { client_visible: !f.client_visible });
      toast.success(!f.client_visible ? "Terlihat oleh klien" : "Disembunyikan dari klien"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="file-upload-widget">
      <div className="flex items-center gap-2 mb-3">
        <input ref={inputRef} type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="hidden" data-testid="file-input"/>
        <Button variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="btn-upload-file">
          <Upload className="w-3.5 h-3.5 mr-1.5"/> {uploading ? "Mengunggah..." : "Unggah File"}
        </Button>
        <span className="text-[11px] text-[#8C9096]">JPG, PNG, PDF · Max 20MB</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {files.length === 0 && <div className="col-span-full text-sm text-[#8C9096] italic text-center py-4">Belum ada file terunggah.</div>}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 p-2 border border-[#E5E0D8] rounded bg-white" data-testid={`file-item-${f.id}`}>
            <FileIcon className="w-8 h-8 text-brand shrink-0"/>
            <div className="flex-1 min-w-0">
              <a href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${localStorage.getItem("dwg_token")}`} target="_blank" rel="noreferrer" className="text-sm font-medium truncate block hover:text-brand" data-testid={`file-link-${f.id}`}>{f.original_filename}</a>
              <div className="text-[10px] text-[#8C9096]">{(f.size / 1024).toFixed(1)} KB · {formatDate(f.created_at)} · {f.uploaded_by_name}</div>
              {f.client_visible && <span className="text-[10px] text-emerald-700 font-semibold">✓ Terlihat oleh klien</span>}
            </div>
            <div className="flex gap-1">
              {canManage && <Button variant="ghost" size="icon" onClick={() => toggleVisibility(f)} title={f.client_visible ? "Sembunyikan dari klien" : "Buat terlihat oleh klien"}>{f.client_visible ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</Button>}
              <Button variant="ghost" size="icon" onClick={() => remove(f.id)}><Trash2 className="w-4 h-4"/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
